import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Input, Segmented, Button, DatePicker, Empty, Spin, message } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import type { Dayjs } from 'dayjs';
import { ReviewPoolItem, ReviewSourceFilter, ReviewSourceTag } from '../types/reviewPool';
import { groupReviewItems } from '../mock/reviewPoolMock';
import {
  GET_REVIEW_POOL_LISTING,
  GetReviewPoolListingResponse,
} from '../graphql/queries/getReviewPoolListing';
import { toReviewPoolItem } from '../utils/reviewPoolAdapter';
import { hubDateKey, hubToday, useHubZone } from '../utils/hubTime';
import ReviewAddressCard from './ReviewAddressCard';

const { RangePicker } = DatePicker;

/** 单页条数：一屏够看又不至于首屏太慢；滚到底继续加载 */
const PAGE_SIZE = 50;
/**
 * 自动加载上限（条）。搜索/筛选是**前端本地**做的（后端 getReviewPoolListing 没有关键字参数），
 * 所以筛选态下必须把剩余页都拉完，否则「站点明明在待上门却搜不到」——这正是之前 pageSize=200
 * 一次性截断留下的坑。这里给个上限只为兜住极端数据量，命中上限会在列表底部显式提示，
 * 不做静默截断。
 */
const MAX_AUTO_LOAD = 3000;
/** 距底部多少 px 触发下一页 */
const SCROLL_THRESHOLD = 120;

interface Props {
  /** 当前 hub（warehouse_id）；无则不查询 */
  hubId?: number;
  /** 外部 resolve 后 +1 触发刷新 */
  reloadToken?: number;
  selectedId?: number;
  onSelect?: (item: ReviewPoolItem) => void;
  /** 回传待处理总数（供页面 Tab 徽标） */
  onCountChange?: (total: number) => void;
  /** 选中项已不在列表里 → 通知页面清空选中，详情栏回初始态（与 DriverFeedbackList 同一口径） */
  onSelectedMissing?: () => void;
}

/**
 * 新地址审核 · 待处理地址列表（P6.2）
 * 搜索(地址/联系方式) + 快捷筛选(全部/系统进单/BD录入/历史存量) + 更多筛选(创建日期区间)
 * + 按 analysisHash 组合分组卡片。数据来自 getReviewPoolListing（按 hub），本地做搜索/筛选/分组。
 *
 * 加载方式：滚动到底自动加载下一页（PAGE_SIZE 一页）。由于搜索/筛选是本地做的，
 * 一旦进入筛选态会自动把剩余页拉完，避免「只加载了首页 → 搜不到实际存在的条目」。
 * 时间相关的展示与筛选统一按 hub 时区（见 utils/hubTime）。
 */
const NewAddressReviewList: React.FC<Props> = ({
  hubId,
  reloadToken,
  selectedId,
  onSelect,
  onCountChange,
  onSelectedMissing,
}) => {
  const { t } = useTranslation();
  const hubZone = useHubZone();
  const [keyword, setKeyword] = useState('');
  const [sourceFilter, setSourceFilter] = useState<ReviewSourceFilter>('all');
  const [showMore, setShowMore] = useState(false);
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const client = useApolloClient();
  const [items, setItems] = useState<ReviewPoolItem[]>([]);
  const [total, setTotal] = useState(0);
  /**
   * 来源页签角标（2.2 需求点6）。**必须来自后端**：列表每页 50 条、首屏只加载一页，
   * 在 `items` 上按 sourceTag 统计出来的是「这 50 条里有几条」，不是真实值。
   * 与 total 同口径——不随关键字/创建日期筛选变化（上方「待处理 N」也是这个口径）。
   */
  const [sourceCounts, setSourceCounts] = useState<Record<ReviewSourceTag, number>>({
    [ReviewSourceTag.System]: 0,
    [ReviewSourceTag.Bd]: 0,
    [ReviewSourceTag.History]: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  /** 已加载到第几页（0 = 还没加载） */
  const [loadedPage, setLoadedPage] = useState(0);
  /** 后端已无更多数据（以「最后一页不满」为准，不能用 items.length>=total 判断：去重会让两者不等） */
  const [exhausted, setExhausted] = useState(false);
  /**
   * 取数失败而停下（≠ 取尽）。与 exhausted 分开存，因为两者在底部提示里说的是完全不同的话：
   * exhausted 说「已全部加载 N 条」，这里要说「加载失败」。混用会让半截列表自称完整。
   * 每次重置（切 hub / 外部刷新）清零。
   */
  const [stoppedByError, setStoppedByError] = useState(false);
  /** 请求序号：切 hub / 外部刷新时丢弃在途响应，避免旧页追加到新列表上 */
  const reqSeq = useRef(0);
  /** 首屏加载完成的标记，供「列表刷新后同步选中项」判断（原来靠 data 引用变化） */
  const [dataToken, setDataToken] = useState(0);

  const fetchPage = useCallback(
    async (nextPage: number, reset: boolean) => {
      if (hubId == null) return;
      const seq = ++reqSeq.current;
      if (reset) setLoading(true);
      else setLoadingMore(true);
      try {
        const { data } = await client.query<GetReviewPoolListingResponse>({
          query: GET_REVIEW_POOL_LISTING,
          variables: { hubId, pageNo: nextPage, pageSize: PAGE_SIZE },
          fetchPolicy: 'network-only',
        });
        if (seq !== reqSeq.current) return;
        const page = (data?.getReviewPoolListing?.items ?? []).map(toReviewPoolItem);
        const listing = data?.getReviewPoolListing;
        setTotal(listing?.total ?? 0);
        // 角标三项**只在第一页有值**：它们与 pageNo 无关，后端为省掉链式翻页时的重复 GROUP BY
        // 扫描（筛选态会一路拉到尽头，灰度 hub ≈60 页），翻页响应里恒返回 0。
        // ⚠️ 所以必须 `if (reset)` —— 不加这个判断，翻第二页就会把首页拿到的数清成 0。
        if (reset) {
          setSourceCounts({
            [ReviewSourceTag.System]: listing?.systemCount ?? 0,
            [ReviewSourceTag.Bd]: listing?.bdCount ?? 0,
            [ReviewSourceTag.History]: listing?.historyCount ?? 0,
          });
        }
        setItems((prev) => {
          // 翻页期间有条目被处理掉会让 offset 前移、同一条落在两页，按 id 去重
          const merged = reset ? page : [...prev, ...page];
          const seen = new Set<number>();
          return merged.filter((it) => (seen.has(it.id) ? false : (seen.add(it.id), true)));
        });
        setLoadedPage(nextPage);
        setExhausted(page.length < PAGE_SIZE);
        setDataToken((v) => v + 1);
      } catch (e) {
        // 这里原本没有 catch —— 取数失败时 rejection 无人处理，loading 在 finally 里被置回 false，
        // 列表渲染成 `<Empty>`「暂无待处理地址」。运营看到的是"今天没活"，不是"加载失败"，于是不会报障。
        // 典型触发：desktop-web 已发新版、api-server 还是旧的（或被单独回滚），
        // 查询里的 systemCount/bdCount/historyCount 不在 schema 里 ⇒ GraphQL 整条校验失败。
        if (seq !== reqSeq.current) return; // 已被更新的请求顶掉：旧请求的失败不该弹给用户
        console.error('review pool listing failed', e);
        message.error(t('pickup.addressReviewPool.list.loadFailed'));
        // **必须置位**：筛选态下 `useEffect(() => { if (isFiltering) loadMore() }, [isFiltering, loadMore])`
        // 会在 loadingMore 由 true 翻回 false 时重新触发；只 catch 不刹车就是「失败→重试→再失败」的
        // 死循环 + 每轮一个 toast。不复用 exhausted 是因为它会让底部显示「已全部加载 N 条」——
        // 明明只加载了一部分，那是在撒谎。
        setStoppedByError(true);
      } finally {
        if (seq === reqSeq.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [client, hubId, t],
  );

  // 切 hub / 外部 resolve 完成 → 从第一页重新加载（列表整体重置，滚动位置自然回顶）
  useEffect(() => {
    if (hubId == null) {
      reqSeq.current += 1;
      setItems([]);
      setTotal(0);
      setLoadedPage(0);
      setExhausted(false);
      setStoppedByError(false);
      return;
    }
    setItems([]);
    setLoadedPage(0);
    setExhausted(false);
    setStoppedByError(false);
    fetchPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubId, reloadToken]);

  // stoppedByError 一并刹住：否则筛选态的链式加载会「失败→重试→再失败」停不下来
  const hasMore = !exhausted && !stoppedByError && loadedPage > 0;
  const reachedAutoCap = items.length >= MAX_AUTO_LOAD;

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore || reachedAutoCap) return;
    fetchPage(loadedPage + 1, false);
  }, [hasMore, loading, loadingMore, reachedAutoCap, fetchPage, loadedPage]);

  // 回传总数（后端 total）供页面 Tab 徽标
  useEffect(() => {
    if (loadedPage > 0) onCountChange?.(total);
  }, [total, loadedPage]);

  const isFiltering =
    keyword.trim() !== '' || sourceFilter !== 'all' || !!(range && (range[0] || range[1]));

  // 筛选态下把剩余页拉完：搜索/筛选在本地做，只加载首页就搜 = 搜不全（假空结果比慢更致命）。
  // 每加载一页 loadedPage 变化会重新触发本 effect，形成链式加载直到取尽或触到上限。
  useEffect(() => {
    if (isFiltering) loadMore();
  }, [isFiltering, loadMore]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const from = range?.[0] ? range[0].format('YYYY-MM-DD') : null;
    const to = range?.[1] ? range[1].format('YYYY-MM-DD') : null;
    return items.filter((it) => {
      if (sourceFilter !== 'all' && it.sourceTag !== sourceFilter) return false;
      if (kw) {
        const hitAddr = it.displayAddress.toLowerCase().includes(kw);
        const hitPhone = it.contactPersons.some((c) => (c.phone || '').includes(kw));
        if (!hitAddr && !hitPhone) return false;
      }
      if (from || to) {
        // 按 hub 当地日期比较：RangePicker 选的是「站点那边的哪一天」，
        // 用 UTC 时间戳跟浏览器本地日边界比会跨天错位
        const day = hubDateKey(it.firstOrderTime, hubZone);
        if (!day) return false;
        if (from && day < from) return false;
        if (to && day > to) return false;
      }
      return true;
    });
  }, [items, keyword, sourceFilter, range, hubZone]);

  // 打开/切回本 tab 时默认选中第一条，避免右侧长期停留空态。
  // 用 filtered（筛选后所见的第一条）而非 items；仅在当前无选中时触发，不打断运营已选的条目。
  useEffect(() => {
    if (selectedId == null && filtered.length > 0) {
      onSelect?.(filtered[0]);
    }
  }, [filtered, selectedId, onSelect]);

  /**
   * 选中项被筛选条件排除掉时清空选中——否则右侧详情会停在一个**列表里根本看不见**的条目上
   * （实测：搜一个池里没有的地址 → 左侧空列表 + 右侧仍展示上一次点开的条目）。
   *
   * 判据必须是「在 items 里、但不在 filtered 里」，不能只判 `!filtered.includes`：
   * 筛选态会链式加载剩余页（见上方 loadMore effect），加载途中选中项可能只是**还没被拉回来**，
   * 此时清空会把运营正在看的详情打空、并被下方 effect 自动改选成 filtered[0]。
   * 「已加载 + 被筛掉」是确定性判断，与还剩多少页无关。
   *
   * 与下方按 dataToken 的同步 effect 分工：那条管「条目已被处理掉/数据变了」，这条管「被筛掉」。
   */
  useEffect(() => {
    if (selectedId == null) return;
    const inItems = items.some((it) => it.id === selectedId);
    const inFiltered = filtered.some((it) => it.id === selectedId);
    if (inItems && !inFiltered) onSelectedMissing?.();
  }, [items, filtered, selectedId, onSelectedMissing]);

  const groups = useMemo(() => groupReviewItems(filtered), [filtered]);

  // 列表刷新后同步选中项：
  //  - 条目已消失（被处理掉）→ 清空选中，详情栏回初始态。只在 resolve 回调里清不够，
  //    任何「没走 resolve 但卡片消失」的路径都会残留陈旧详情。
  //  - 条目还在但数据变了（如改地址后重诊断）→ 回抛最新对象，否则右侧详情会停在旧地址/旧诊断。
  // 只依赖 dataToken（每次成功取数 +1），所以每次刷新只回抛一次，不会自激。
  // 且只在「重置为第一页」的刷新后校验（loadedPage===1）：滚动追加页时选中项本来就可能还在
  // 后面没加载，拿 onSelectedMissing 清掉会把运营正在看的详情打空。
  useEffect(() => {
    if (dataToken === 0 || loadedPage !== 1 || selectedId == null) return;
    const fresh = items.find((it) => it.id === selectedId);
    if (fresh) {
      onSelect?.(fresh);
    } else {
      onSelectedMissing?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataToken]);

  const pendingTotal = loadedPage > 0 ? total : items.length;
  const todayNewCount = useMemo(() => {
    // 「今日」= hub 当地今天；已加载条目内统计（列表按 id 倒序，新增的都在最前，首页即可覆盖）
    const today = hubToday(hubZone);
    return items.filter((it) => hubDateKey(it.firstOrderTime, hubZone) === today).length;
  }, [items, hubZone]);

  // 「历史存量」筛选项 2026-08-20 曾下架（理由：历史存量条目的 AB 映射没有可行方案，
  // 先不让业务从审核池操作这些卡片），2.1 需求点2 给出方案后恢复：reviewOperations 已为
  // 历史存量的「相似」与「新地址」两个分支都挂上 AbMapping（整站作废语义）。
  //
  // ⚠️ 恢复筛选项只解决「筛得到」，条目本身还被数据侧的 is_deleted=1 挡着
  // （列表 findActivePage / 计数 countActivePage / 去重 findActiveForDedup 都带 is_deleted=0）。
  // 必须同时调 hideHistoryStockReviewPool(restore: true) 把 is_deleted 刷回 0，
  // 否则这里点进去仍是空列表。
  /**
   * 2.2 需求点6：来源页签带数量。
   *
   * 「全部」**刻意不带** —— 它的数就是紧邻上方那行的「待处理 N」，再印一遍只是噪音（产品 2026-10-14 定）。
   * 其余三项恒显示数字（含 0）：隐藏 0 会让「这里确实一条都没有」和「这个版本还没做角标」长得一样。
   */
  const withCount = (label: React.ReactNode, n: number) => (
    <span>
      {label}
      {/* `loadedPage > 0` 这道闸与上方 pendingTotal 同理：切 hub 时 items 被同步清空、
          新 hub 的响应还在路上，这段窗口里 sourceCounts 还是**上一个 hub** 的数。
          不加闸运营会看到「空列表 + 系统进单(1234)」。loadedPage 在重置时归 0，响应回来才置回 1。 */}
      {/* text-xs(12px) 与 DriverFeedbackList 保持一致（那边有实测数据与理由）。
          这一行本来 14px 也放得下，统一字号是为了两行页签看起来是同一个东西。 */}
      <span className="ml-0.5 text-xs text-gray-400">({loadedPage > 0 ? n : 0})</span>
    </span>
  );

  const filterOptions = [
    { label: t('pickup.addressReviewPool.sourceTag.all'), value: 'all' as const },
    {
      label: withCount(
        t('pickup.addressReviewPool.sourceTag.system'),
        sourceCounts[ReviewSourceTag.System],
      ),
      value: ReviewSourceTag.System,
    },
    {
      label: withCount(t('pickup.addressReviewPool.sourceTag.bd'), sourceCounts[ReviewSourceTag.Bd]),
      value: ReviewSourceTag.Bd,
    },
    {
      label: withCount(
        t('pickup.addressReviewPool.sourceTag.history'),
        sourceCounts[ReviewSourceTag.History],
      ),
      value: ReviewSourceTag.History,
    },
  ];

  const handleToggleMore = () => {
    const next = !showMore;
    setShowMore(next);
    // 收起时自动清空（PRD）
    if (!next) setRange(null);
  };

  return (
    <div className="flex h-full flex-col">
      {/* 标题行：📞 待处理地址 [待处理 N]　今日新增 M */}
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-purple-50">📞</span>
        <span className="text-[15px] font-semibold text-gray-800">
          {t('pickup.addressReviewPool.list.pendingTitle')}
        </span>
        <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-600">
          {t('pickup.addressReviewPool.list.pending')} {pendingTotal}
        </span>
        <span className="ml-auto text-xs text-gray-400">
          {t('pickup.addressReviewPool.list.todayNew')} {todayNewCount}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-gray-400" />}
          placeholder={t('pickup.addressReviewPool.list.searchPlaceholder') as string}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
        <Button
          icon={<FilterOutlined />}
          type={showMore ? 'primary' : 'default'}
          onClick={handleToggleMore}
        >
          {t('pickup.addressReviewPool.list.moreFilter')}
        </Button>
      </div>

      {/* 与 DriverFeedbackList 同一道兜底（见那边的长注释）。这一行只有 4 个页签、文案短，
          2026-10-14 实测 1366 下 379px / 容器 420px，角标到 4 位数也还剩 6px ——
          **当前不会溢出**。加这个是为了别让两处的处理方式不一致：
          下一个人改宽度或文案时，只会看他改的那一处。 */}
      <div className="mt-2 overflow-x-auto">
        <Segmented
          options={filterOptions as any}
          value={sourceFilter}
          onChange={(v) => setSourceFilter(v as ReviewSourceFilter)}
        />
      </div>

      {showMore && (
        <div className="mt-2">
          <RangePicker
            className="w-full"
            value={range as any}
            onChange={(v) => setRange(v as [Dayjs | null, Dayjs | null] | null)}
            placeholder={[
              t('pickup.addressReviewPool.list.dateRange') as string,
              t('pickup.addressReviewPool.list.dateRange') as string,
            ]}
          />
        </div>
      )}

      <div
        className="mt-3 flex-1 overflow-auto pr-1"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD) loadMore();
        }}
      >
        {loading ? (
          <div className="mt-24 flex justify-center">
            <Spin />
          </div>
        ) : groups.length === 0 ? (
          // 区分「本来就没有待处理」与「筛选后无匹配」：后者说"暂无待处理地址"会让运营以为队列空了，
          // 而实际是搜索词没命中（搜索只匹配标准化后地址 + 电话）
          <Empty
            description={t(
              isFiltering
                ? 'pickup.addressReviewPool.list.emptyFiltered'
                : 'pickup.addressReviewPool.list.empty',
            )}
            className="mt-24"
          />
        ) : (
          groups.map((g) => {
            const cards = g.items.map((it) => (
              <ReviewAddressCard
                key={it.id}
                item={it}
                selected={it.id === selectedId}
                onSelect={onSelect}
              />
            ));
            // 组合（同 analysisHash 多条）外部加框
            return g.items.length > 1 ? (
              <div
                key={g.analysisHash}
                className="mb-3 space-y-2 rounded-lg border border-dashed border-purple-300 bg-purple-50/30 p-2"
              >
                {cards}
              </div>
            ) : (
              <div key={g.analysisHash} className="mb-3">
                {cards}
              </div>
            );
          })
        )}

        {/* 底部状态：加载中 / 命中自动加载上限（显式提示，不静默截断） / 已全部加载 */}
        {!loading && loadedPage > 0 && (
          <div className="pb-2 pt-1 text-center text-xs text-gray-400">
            {loadingMore ? (
              <Spin size="small" />
            ) : stoppedByError ? (
              // 必须排在 allLoaded 前面：加载到一半失败时列表是**不完整**的，
              // 说「已全部加载 300 条」会让运营按一份残缺名单去排活
              <span className="text-red-500">
                {t('pickup.addressReviewPool.list.loadFailed')}
              </span>
            ) : reachedAutoCap && hasMore ? (
              t('pickup.addressReviewPool.list.loadCapped', { total: items.length })
            ) : hasMore ? (
              t('pickup.addressReviewPool.list.scrollForMore')
            ) : isFiltering ? (
              // 筛选态必须报「匹配数」：只报 items.length 会出现"列表一条都没有、底下却写着已全部加载 259 条"，
              // 看起来像加载失败。matched 才是运营眼前看到的条数。
              t('pickup.addressReviewPool.list.allLoadedFiltered', {
                matched: filtered.length,
                total: items.length,
              })
            ) : (
              t('pickup.addressReviewPool.list.allLoaded', { total: items.length })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewAddressReviewList;
